/**
 * Sector Color Utility
 * Provides consistent color assignment for sectors across the entire dashboard
 * 
 * Features:
 * - Dynamic color assignment based on available sectors
 * - Consistent colors using string hashing
 * - Priority handling for common sectors
 * - Professional color palette optimized for dark dashboard backgrounds
 */

// Professional color palette optimized for dashboard visibility
// Colors are chosen for good contrast against dark background and visual distinction
const SECTOR_COLOR_PALETTE = [
  '#FF6B6B',  // Coral Red - High visibility
  '#4ECDC4',  // Turquoise - Cool and professional
  '#45B7D1',  // Sky Blue - Trust and reliability
  '#96CEB4',  // Sage Green - Growth and stability
  '#FECA57',  // Golden Yellow - Energy and attention
  '#A55EEA',  // Purple - Innovation and creativity
  '#26D0CE',  // Bright Teal - Modern and fresh
  '#FD79A8',  // Pink - Distinctive and modern
  '#FDCB6E',  // Warm Orange - Friendly and approachable
  '#6C5CE7',  // Violet - Premium and sophisticated
  '#A29BFE',  // Light Purple - Calm and professional
  '#74B9FF',  // Light Blue - Clear and trustworthy
  '#00B894',  // Emerald - Success and growth
  '#E17055',  // Terracotta - Warm and earthy
  '#81ECEC',  // Aqua - Fresh and clean
  '#FAB1A0',  // Peach - Soft and approachable
  '#00CEC9',  // Teal - Balance and clarity
  '#E84393',  // Magenta - Bold and confident
  '#FDCB6E',  // Amber - Warm and inviting
  '#6C5CE7'   // Deep Purple - Luxury and depth
];

// Common sectors that should get priority color assignments
const PRIORITY_SECTORS = ['CGF', 'LATAM', 'Europe', 'PFNA', 'PBNA', 'AMESA', 'APAC'];

/**
 * Generate consistent color for a sector using string hashing
 * This ensures the same sector always gets the same color across sessions
 */
const generateSectorHash = (sectorName: string): number => {
  let hash = 0;
  for (let i = 0; i < sectorName.length; i++) {
    const char = sectorName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Generate color mapping for sectors with priority handling
 * @param sectors Array of sector names
 * @returns Object mapping sector names to hex colors
 */
export const generateSectorColors = (sectors: string[]): { [key: string]: string } => {
  const colorMap: { [key: string]: string } = {};
  const usedColors = new Set<string>();
  
  // First, assign colors to priority sectors to ensure they get distinct colors
  PRIORITY_SECTORS.forEach((sector, index) => {
    if (sectors.includes(sector)) {
      const color = SECTOR_COLOR_PALETTE[index % SECTOR_COLOR_PALETTE.length];
      colorMap[sector] = color;
      usedColors.add(color);
    }
  });

  // Then assign colors to remaining sectors
  sectors.forEach(sector => {
    if (!colorMap[sector]) {
      const hash = generateSectorHash(sector);
      let colorIndex = hash % SECTOR_COLOR_PALETTE.length;
      let color = SECTOR_COLOR_PALETTE[colorIndex];
      
      // If color is already used by a priority sector, try next available
      let attempts = 0;
      while (usedColors.has(color) && attempts < SECTOR_COLOR_PALETTE.length) {
        colorIndex = (colorIndex + 1) % SECTOR_COLOR_PALETTE.length;
        color = SECTOR_COLOR_PALETTE[colorIndex];
        attempts++;
      }
      
      colorMap[sector] = color;
      usedColors.add(color);
    }
  });

  return colorMap;
};

/**
 * Get a single color for a specific sector
 * @param sectorName Name of the sector
 * @returns Hex color string
 */
export const getSectorColor = (sectorName: string): string => {
  // Check if it's a priority sector
  const priorityIndex = PRIORITY_SECTORS.indexOf(sectorName);
  if (priorityIndex !== -1) {
    return SECTOR_COLOR_PALETTE[priorityIndex % SECTOR_COLOR_PALETTE.length];
  }
  
  // Generate hash-based color for non-priority sectors
  const hash = generateSectorHash(sectorName);
  const colorIndex = hash % SECTOR_COLOR_PALETTE.length;
  return SECTOR_COLOR_PALETTE[colorIndex];
};

/**
 * Get the entire color palette (useful for legend generation)
 * @returns Array of hex color strings
 */
export const getColorPalette = (): string[] => {
  return [...SECTOR_COLOR_PALETTE];
};

/**
 * Get priority sectors list
 * @returns Array of priority sector names
 */
export const getPrioritySectors = (): string[] => {
  return [...PRIORITY_SECTORS];
};
